import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { skills, experience, education } from '../../data/skills';

const About = () => {
  const sectionRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const rotateX = useSpring(useTransform(mouseY, [-300, 300], [10, -10]), { stiffness: 100, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-300, 300], [-10, 10]), { stiffness: 100, damping: 30 });
  
  const handleMouseMove = useCallback((e) => {
    if (!sectionRef.current) return;
    
    try {
      const rect = sectionRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    } catch (error) {
      console.warn('Mouse tracking error:', error);
    }
  }, [mouseX, mouseY]);
  
  useEffect(() => {
    let timeoutId;
    const throttledMouseMove = (e) => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleMouseMove(e);
        timeoutId = null;
      }, 16); // ~60fps
    };
    
    window.addEventListener('mousemove', throttledMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', throttledMouseMove);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleMouseMove]);

  const skillCategories = useMemo(() => Object.entries(skills), []);

  return (
    <section id="about" className="about-futuristic" ref={sectionRef}>
      <div className="neural-network-bg"></div>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="section-header-futuristic"
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        >
          <motion.h2 
            className="section-title-futuristic"
            whileHover={{ scale: 1.05, textShadow: "0 0 30px #00f5ff" }}
          >
            About Me
          </motion.h2>
          <motion.p 
            className="section-subtitle-futuristic"
            whileHover={{ scale: 1.02 }}
          >
            A Software Developer based in India, crafting digital solutions that bridge innovation and functionality
          </motion.p>
        </motion.div>
        
        <div className="about-content-futuristic">
          <motion.div 
            className="hologram-card experience-card"
            initial={{ opacity: 0, rotateY: -90 }}
            whileInView={{ opacity: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            whileHover={{ 
              scale: 1.02, 
              rotateY: 5,
              boxShadow: "0 20px 40px rgba(0, 245, 255, 0.3)"
            }}
          >
            <div className="card-glow"></div>
            <h3 className="card-title">Experience Matrix</h3>
            <div className="experience-item">
              <div className="experience-header">
                <motion.h4 whileHover={{ color: "#00f5ff" }}>{experience.position}</motion.h4>
                <p className="company">{experience.company}</p>
                <p className="duration">{experience.duration}</p>
              </div>
              <div className="experience-description">
                <p>{experience.description}</p>
                <motion.p 
                  className="catchy-text"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ✨ Your future colleague ✨
                </motion.p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="hologram-card skills-card"
            initial={{ opacity: 0, rotateY: 90 }}
            whileInView={{ opacity: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            whileHover={{ 
              scale: 1.02, 
              rotateY: -5,
              boxShadow: "0 20px 40px rgba(191, 0, 255, 0.3)"
            }}
          >
            <div className="card-glow purple"></div>
            <h3 className="card-title">Tech Arsenal</h3>
            <div className="skills-matrix">
              {skillCategories.map(([category, skillList], categoryIndex) => (
                <div key={`${category}-${categoryIndex}`} className="skill-category-futuristic">
                  <motion.h4 
                    whileHover={{ scale: 1.1, color: "#bf00ff" }}
                    className="category-title"
                  >
                    {category.toUpperCase()}
                  </motion.h4>
                  <div className="skill-orbs">
                    {skillList.map((skill, skillIndex) => (
                      <motion.div
                        key={`${skill}-${skillIndex}`}
                        className="skill-orb"
                        whileHover={{ 
                          scale: 1.3, 
                          rotate: 360,
                          boxShadow: "0 0 20px #00f5ff"
                        }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {skill}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            className="hologram-card education-card"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 20px 40px rgba(16, 185, 129, 0.3)"
            }}
          >
            <div className="card-glow green"></div>
            <h3 className="card-title">Knowledge Base</h3>
            <div className="education-timeline">
              {education.map((edu, eduIndex) => (
                <motion.div 
                  key={`${edu.degree}-${eduIndex}`} 
                  className="timeline-item"
                  whileHover={{ x: 10, scale: 1.02 }}
                >
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>{edu.degree}</h4>
                    <p>{edu.institution}</p>
                    {edu.university && <p>{edu.university}</p>}
                    <span className="duration">{edu.duration}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;